// ─── components/Admin/UsersPanel.jsx ───────────────────────────────────────
// Admin-only user management. CRUD over /api/admin/users.
// Rendered only when the logged-in user has the `manage_users` permission
// (App.js gates the nav tab + page body on it).
//
// All calls go through apiFetch (bearer token + refresh-on-401).
// ───────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/apiClient';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

// Roles the UI offers. Must match backend permissions.py VALID_ROLES.
const ROLES = [
  { value: 'admin',      label: 'Admin',      color: '#8b5cf6', desc: 'Full access + user management' },
  { value: 'analyst_t1', label: 'Analyst T1', color: '#3b82f6', desc: 'Urgent auto-alert queue' },
  { value: 'analyst_t2', label: 'Analyst T2', color: '#06b6d4', desc: 'Review queue' },
  { value: 'guest',      label: 'Guest',      color: '#6b7280', desc: 'Read-only observer' },
];
const roleMeta = (v) => ROLES.find(r => r.value === v) || { label: v, color: '#6b7280' };

const card   = { background: '#141720', border: '1px solid #ffffff14', borderRadius: 10 };
const input  = { width: '100%', padding: '8px 11px', background: '#0d1018', border: '1px solid #ffffff22', borderRadius: 6, color: '#e8eaf0', fontSize: 13, ...mono, outline: 'none', boxSizing: 'border-box' };
const btn    = (c = '#3b82f6') => ({ padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: `1px solid ${c}44`, background: `${c}22`, color: c, ...mono });

export default function UsersPanel() {
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [notice, setNotice] = useState('');

  // create form
  const [showCreate, setShowCreate] = useState(false);
  const [nu, setNu] = useState({ username: '', password: '', role: 'analyst_t1', must_change_password: true });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/admin/users');
      if (res.status === 403) { setError('You do not have permission to manage users.'); setUsers([]); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      setError('Failed to load users. Is the backend reachable?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 3500); };

  async function createUser() {
    setError('');
    if (nu.username.length < 3) { setError('Username must be at least 3 characters.'); return; }
    if (nu.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setCreating(true);
    try {
      const res = await apiFetch('/admin/users', {
        method: 'POST',
        body: JSON.stringify(nu),
      });
      if (res.status === 409) { setError('That username already exists.'); return; }
      if (res.status === 403) { setError('You do not have permission to create users.'); return; }
      if (!res.ok) {
        let d = `HTTP ${res.status}`;
        try { d = (await res.json()).detail || d; } catch {}
        setError(typeof d === 'string' ? d : 'Create failed.'); return;
      }
      flash(`Created user "${nu.username}".`);
      setNu({ username: '', password: '', role: 'analyst_t1', must_change_password: true });
      setShowCreate(false);
      load();
    } catch (e) {
      setError('Create failed — network or server error.');
    } finally {
      setCreating(false);
    }
  }

  async function changeRole(user, role) {
    setError('');
    try {
      const res = await apiFetch(`/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        let d = `HTTP ${res.status}`;
        try { d = (await res.json()).detail || d; } catch {}
        setError(typeof d === 'string' ? d : 'Role change failed.'); return;
      }
      flash(`Updated role for "${user.username}".`);
      load();
    } catch { setError('Role change failed.'); }
  }

  async function toggleActive(user) {
    setError('');
    try {
      const res = await apiFetch(`/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !user.is_active }),
      });
      if (!res.ok) {
        let d = `HTTP ${res.status}`;
        try { d = (await res.json()).detail || d; } catch {}
        setError(typeof d === 'string' ? d : 'Status change failed.'); return;
      }
      flash(`${user.is_active ? 'Deactivated' : 'Reactivated'} "${user.username}".`);
      load();
    } catch { setError('Status change failed.'); }
  }

  async function resetPassword(user) {
    const np = window.prompt(`New password for "${user.username}" (min 8 chars):`);
    if (np == null) return;
    if (np.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError('');
    try {
      const res = await apiFetch(`/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ new_password: np }),
      });
      if (!res.ok) {
        let d = `HTTP ${res.status}`;
        try { d = (await res.json()).detail || d; } catch {}
        setError(typeof d === 'string' ? d : 'Password reset failed.'); return;
      }
      flash(`Password reset for "${user.username}" — they must change it on next login.`);
      load();
    } catch { setError('Password reset failed.'); }
  }

  async function deactivate(user) {
    if (!window.confirm(`Deactivate "${user.username}"? They will be signed out and unable to log in.`)) return;
    setError('');
    try {
      const res = await apiFetch(`/admin/users/${user.id}`, { method: 'DELETE' });
      if (!res.ok) {
        let d = `HTTP ${res.status}`;
        try { d = (await res.json()).detail || d; } catch {}
        setError(typeof d === 'string' ? d : 'Deactivate failed.'); return;
      }
      flash(`Deactivated "${user.username}".`);
      load();
    } catch { setError('Deactivate failed.'); }
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>User Management</div>
          <div style={{ fontSize: 11, color: '#6b7280', ...mono, marginTop: 2 }}>Create and manage SOC accounts and roles</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btn('#9ca3af')}>↻ Refresh</button>
          <button onClick={() => { setShowCreate(v => !v); setError(''); }} style={btn('#10b981')}>
            {showCreate ? '× Cancel' : '+ New User'}
          </button>
        </div>
      </div>

      {notice && (
        <div style={{ padding: '8px 12px', background: '#10b98118', border: '1px solid #10b98144', borderRadius: 6, fontSize: 12, color: '#34d399', ...mono, marginBottom: 12 }}>
          {notice}
        </div>
      )}
      {error && (
        <div style={{ padding: '8px 12px', background: '#ff3b4e18', border: '1px solid #ff3b4e44', borderRadius: 6, fontSize: 12, color: '#ff6b7a', ...mono, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {showCreate && (
        <div style={{ ...card, padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Create new user</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 5 }}>USERNAME</div>
              <input style={input} value={nu.username} onChange={e => setNu({ ...nu, username: e.target.value })} placeholder="e.g. analyst_jane" />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 5 }}>PASSWORD (min 8)</div>
              <input style={input} type="text" value={nu.password} onChange={e => setNu({ ...nu, password: e.target.value })} placeholder="temporary password" />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 6 }}>ROLE</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ROLES.map(r => (
                <button key={r.value} onClick={() => setNu({ ...nu, role: r.value })}
                  style={{ ...btn(r.color), opacity: nu.role === r.value ? 1 : 0.45, borderWidth: nu.role === r.value ? 2 : 1 }}
                  title={r.desc}>
                  {r.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginTop: 6 }}>
              {roleMeta(nu.role).desc}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#9ca3af', ...mono, marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={nu.must_change_password} onChange={e => setNu({ ...nu, must_change_password: e.target.checked })} />
            Require password change on first login
          </label>
          <button onClick={createUser} disabled={creating} style={{ ...btn('#10b981'), opacity: creating ? 0.5 : 1 }}>
            {creating ? '⟳ Creating...' : 'Create user'}
          </button>
        </div>
      )}

      <div style={{ ...card, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#0f1117', color: '#6b7280', ...mono, textAlign: 'left' }}>
              <th style={{ padding: '10px 14px', fontWeight: 500 }}>USER</th>
              <th style={{ padding: '10px 14px', fontWeight: 500 }}>ROLE</th>
              <th style={{ padding: '10px 14px', fontWeight: 500 }}>STATUS</th>
              <th style={{ padding: '10px 14px', fontWeight: 500 }}>LAST LOGIN</th>
              <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280', ...mono }}>Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280', ...mono }}>No users.</td></tr>
            )}
            {!loading && users.map(u => {
              const rm = roleMeta(u.role);
              return (
                <tr key={u.id} style={{ borderTop: '1px solid #ffffff0d', opacity: u.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontWeight: 600, color: '#e8eaf0' }}>{u.username}</div>
                    {u.must_change_password && (
                      <div style={{ fontSize: 9, color: '#f59e0b', ...mono, marginTop: 2 }}>must change password</div>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <select value={u.role} onChange={e => changeRole(u, e.target.value)}
                      style={{ ...input, width: 'auto', padding: '5px 8px', color: rm.color, borderColor: `${rm.color}44` }}>
                      {ROLES.map(r => <option key={r.value} value={r.value} style={{ color: '#e8eaf0', background: '#0d1018' }}>{r.label}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: 11, ...mono, color: u.is_active ? '#34d399' : '#6b7280' }}>
                      {u.is_active ? '● active' : '○ inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#9ca3af', ...mono, fontSize: 11 }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => resetPassword(u)} style={{ ...btn('#f59e0b'), padding: '4px 9px', marginRight: 6 }}>Reset PW</button>
                    <button onClick={() => toggleActive(u)} style={{ ...btn(u.is_active ? '#9ca3af' : '#34d399'), padding: '4px 9px', marginRight: 6 }}>
                      {u.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                    <button onClick={() => deactivate(u)} style={{ ...btn('#ff3b4e'), padding: '4px 9px' }}>Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 10, color: '#4b5563', ...mono, marginTop: 12 }}>
        Note: "Remove" and "Deactivate" are soft — the account is disabled, not deleted, and its audit history is preserved.
        The last active admin cannot be demoted or deactivated.
      </div>
    </div>
  );
}
